import React, {FC, useEffect, useState} from "react"
import {Grid} from "react-bootstrap"
import styled, {css} from "styled-components"

import {FormattedMessage} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {DealBrand} from "@irati/types"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {getColorFromThumbnail} from "@irati/utils/images"

interface CommonProps {
  dealBrand: DealBrand
}

interface Props extends CommonProps {
  totalResultsCount?: number
}

const BrandBanner: FC<Props> = ({dealBrand, totalResultsCount}) => {
  const theme = useTheme()
  const [dealColor, setDealColor] = useState<string>(theme.colors.perkbox.blue)

  useEffect(() => {
    const {logoUrl} = dealBrand
    getColorFromThumbnail(logoUrl).then((color) => {
      setDealColor(color)
    })
  }, [])

  return (
    <Background dealBrand={dealBrand} bgColor={dealColor}>
      <Gradient />
      <Grid style={{position: "relative", height: "100%"}}>
        <ResponsiveFlex>
          <Flex alignItems="center" gap={2}>
            <BrandLogo img={dealBrand.logoUrl} alt="" />
            <Flex direction="column">
              <H2 marginBottom={1} color={theme.colors.greys.white}>
                {dealBrand.label}
              </H2>
              <Body color={theme.colors.greys.white}>
                <FormattedMessage id="perks.brand.brandPage.banner.availableDeals" values={{count: totalResultsCount}} />
              </Body>
            </Flex>
          </Flex>
          <Description small color={theme.colors.greys.white}>
            {dealBrand.description}
          </Description>
        </ResponsiveFlex>
      </Grid>
    </Background>
  )
}

export default BrandBanner

interface BackgroundProps extends CommonProps {
  bgColor: string
}

const Background = styled.div<BackgroundProps>`
  ${({dealBrand, bgColor, theme}) => css`
    position: relative;
    background-image: url(${dealBrand.bannerUrl});
    background-color: ${bgColor};
    background-position: center;
    background-size: cover;
    height: 240px;
    margin-bottom: ${theme.spacing.three};
    ${theme.mediaQueries.minWidthMedia.tablet`
      height: 163px;
      margin-bottom: ${theme.spacing.stepSize * 4.5}px;
    `}
  `}
`

const ResponsiveFlex = styled(Flex)`
  ${({theme}) => css`
    margin-top: ${theme.spacing.stepSize * 8}px;
    gap: ${theme.spacing.six};
    flex-direction: column;
    ${theme.mediaQueries.minWidthMedia.tablet`
      margin-top: 0;
      flex-direction: row;
      align-items: center;
      height: 100%;
    `}
  `}
`

const Description = styled(Body)`
  ${({theme}) => css`
    max-height: 20px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    ${theme.mediaQueries.minWidthMedia.tablet`
       max-height: 120px;
       white-space: normal;
    `}
  `}
`

interface LogoProps {
  img: string
}

const BrandLogo = styled.div<LogoProps>`
  ${({theme, img}) => css`
    width: 72px;
    height: 72px;
    border-radius: 36px;
    overflow: hidden;
    background: url(${img}) center;
    background-size: cover;
    flex-shrink: 0;
    ${theme.mediaQueries.minWidthMedia.tablet`
      width: 120px;
      height: 120px;
      border-radius: 60px;
    `}
  `}
`

const Gradient = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.55) 60%);
`
